/**
 * Offline tolerance for kitchen actions (kitchen flow, lane C).
 *
 * The kitchen tablet is on a weak connection. A kitchen action (send a request,
 * receive, send a return, cancel) is given an Idempotency-Key BEFORE the first
 * attempt. If the attempt fails for a transport reason (no response, timeout,
 * 5xx) the action is queued in localStorage under that same key and retried
 * later; the server treats a repeated key as a no-op returning the original
 * result, so a retry of an attempt that actually landed can never double-issue
 * or double-receive. A refusal (4xx) is NOT queued — retrying would be refused
 * the same way — it is shown to the person instead.
 *
 * Storage can be missing or throw (private mode, blocked site data), so every
 * read and write is wrapped; the queue then simply lives for the page.
 *
 * Pilot fix (Batch 5 lane tablet):
 *  - A server error (5xx) that repeats is retried at most MAX_SERVER_ATTEMPTS
 *    times with a growing wait, then the action is marked failed ("Couldn't
 *    send — try again or discard") and the rest of the queue keeps flushing.
 *    Order is kept per document only: an action waits behind a failed or
 *    not-yet-due one on the same document (e.g. a receive behind the cancel of
 *    the same request), never behind another document's. No connection at all
 *    still stops the flush and never counts towards failing an action.
 *  - Each action remembers who saved it. The tablet is shared; an action saved
 *    by someone else is never sent under the signed-in person's login — it is
 *    held and shown ("Saved by Ali on this tablet") until Ali signs in again,
 *    or someone discards it.
 */

export type QueuedMethod = 'POST' | 'PUT' | 'PATCH'

export interface QueuedAction {
  /** Doubles as the Idempotency-Key. */
  id: string
  /** Plain words for the "not synced" list, e.g. "Receive REQ-20260925-004". */
  label: string
  method: QueuedMethod
  path: string
  body: unknown
  createdAt: number
  attempts: number
  lastError?: string
  /** Who saved it (users.id). Never sent under anyone else's login. */
  userId?: string
  /** Their name, for "Saved by … on this tablet". */
  userName?: string
  /** Server errors so far; at MAX_SERVER_ATTEMPTS the action is failed. */
  serverAttempts?: number
  /** Not retried by itself before this time (ms since epoch). */
  nextAttemptAt?: number
  /** Gave up retrying by itself: waits for "Try again" or "Discard". */
  failed?: boolean
}

/** Server errors in a row before an action is shown as failed. */
export const MAX_SERVER_ATTEMPTS = 5
const BACKOFF_BASE_MS = 30_000
const BACKOFF_MAX_MS = 8 * 60_000

/** The wait before the next try after `serverAttempts` server errors. */
export function backoffMs(serverAttempts: number): number {
  if (serverAttempts <= 0) return 0
  return Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** (serverAttempts - 1))
}

/**
 * The document an action belongs to, for ordering: actions on the same
 * document (".../requisitions/<id>/submit", ".../requisitions/<id>/receive")
 * go in order; a create ("POST /inventory-flow/requisitions") depends on
 * nothing queued before it, so it is its own.
 */
export function resourceKey(a: Pick<QueuedAction, 'path' | 'id'>): string {
  const parts = a.path.split('?')[0].split('/').filter(Boolean)
  return parts.length >= 3 ? parts.slice(0, 3).join('/') : `${parts.join('/')}#${a.id}`
}

/** The signed-in person on this tablet (from pos_user), or null. */
export function currentQueueUser(storage: KVStorage | null = defaultStorage()): { id: string; name: string } | null {
  try {
    const raw = storage?.getItem('pos_user')
    if (!raw) return null
    const u = JSON.parse(raw) as { id?: string | number; first_name?: string; last_name?: string; username?: string }
    if (u.id == null || String(u.id) === '') return null
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.username || ''
    return { id: String(u.id), name }
  } catch {
    return null
  }
}

/** Saved by someone other than `userId` (or by an unknown person). */
export function isOtherUsers(a: QueuedAction, userId: string | null | undefined): boolean {
  return (a.userId ?? '') !== (userId ?? '')
}

/** The queue split for the screen: mine still to send, mine failed, others' held. */
export function splitQueue(queue: QueuedAction[], userId: string | null | undefined) {
  const mine = queue.filter((a) => !isOtherUsers(a, userId))
  return {
    waiting: mine.filter((a) => !a.failed),
    failed: mine.filter((a) => a.failed),
    held: queue.filter((a) => isOtherUsers(a, userId)),
  }
}

export interface KVStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

export const QUEUE_STORAGE_KEY = 'bhookly.inventoryFlow.queue.v1'

let memoryFallback: QueuedAction[] = []

function defaultStorage(): KVStorage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

export function newIdempotencyKey(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through */
  }
  return `ifk-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

export function loadQueue(storage: KVStorage | null = defaultStorage()): QueuedAction[] {
  if (!storage) return [...memoryFallback]
  try {
    const raw = storage.getItem(QUEUE_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (a): a is QueuedAction =>
        !!a && typeof a === 'object' && typeof (a as QueuedAction).id === 'string' && typeof (a as QueuedAction).path === 'string',
    )
  } catch {
    return [...memoryFallback]
  }
}

export function saveQueue(queue: QueuedAction[], storage: KVStorage | null = defaultStorage()): void {
  memoryFallback = [...queue]
  if (!storage) return
  try {
    storage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue))
  } catch {
    /* storage full or blocked — memoryFallback keeps it for this page */
  }
}

export function enqueueAction(action: QueuedAction, storage: KVStorage | null = defaultStorage()): QueuedAction[] {
  const q = loadQueue(storage).filter((a) => a.id !== action.id)
  q.push(action)
  saveQueue(q, storage)
  return q
}

export function removeAction(id: string, storage: KVStorage | null = defaultStorage()): QueuedAction[] {
  const q = loadQueue(storage).filter((a) => a.id !== id)
  saveQueue(q, storage)
  return q
}

/** The subset of ApiClientError the queue needs to classify a failure. */
export interface FailureLike {
  isNetworkError?: boolean
  status?: number
  message?: string
}

/** Worth retrying later: no response at all, a timeout, or a server error. */
export function isRetryableFailure(err: unknown): boolean {
  const e = err as FailureLike | null
  if (!e || typeof e !== 'object') return false
  if (e.isNetworkError) return true
  return typeof e.status === 'number' && (e.status >= 500 || e.status === 408 || e.status === 429)
}

export interface FlushResult {
  sent: string[]
  stillQueued: number
  /** Refused by the server on retry — removed, with the reason, for the UI. */
  refused: { action: QueuedAction; message: string }[]
  /** Gave up on this flush after MAX_SERVER_ATTEMPTS server errors. */
  failed: QueuedAction[]
}

export interface FlushOptions {
  /**
   * The signed-in person. When given, only their actions are sent; anyone
   * else's are held. Omitted: no owner check (tests, legacy callers).
   */
  userId?: string | null
  now?: number
  /** "Try again now": ignore the wait between server-error retries. */
  force?: boolean
}

/**
 * Replays queued actions oldest first. Stops at the first transport failure
 * (the connection is still down — no point hammering it); drops an action the
 * server refuses, reporting it; after repeated server errors marks an action
 * failed and carries on with other documents' actions.
 */
export async function flushQueue(
  send: (a: QueuedAction) => Promise<unknown>,
  storage: KVStorage | null = defaultStorage(),
  opts: FlushOptions = {},
): Promise<FlushResult> {
  const result: FlushResult = { sent: [], stillQueued: 0, refused: [], failed: [] }
  const now = opts.now ?? Date.now()
  const checkOwner = opts.userId !== undefined
  const queue = loadQueue(storage).sort((a, b) => a.createdAt - b.createdAt)
  // Documents with an earlier action still waiting: later ones wait too.
  const blocked = new Set<string>()
  for (const action of queue) {
    const res = resourceKey(action)
    if (blocked.has(res)) continue
    if ((checkOwner && isOtherUsers(action, opts.userId)) || action.failed || (!opts.force && (action.nextAttemptAt ?? 0) > now)) {
      blocked.add(res)
      continue
    }
    try {
      await send(action)
      removeAction(action.id, storage)
      result.sent.push(action.id)
    } catch (err) {
      const message = (err as FailureLike)?.message
      if (isRetryableFailure(err)) {
        const transport = !!(err as FailureLike)?.isNetworkError
        let failedNow: QueuedAction | null = null
        const q = loadQueue(storage).map((a) => {
          if (a.id !== action.id) return a
          const next: QueuedAction = { ...a, attempts: a.attempts + 1, lastError: message ?? 'Not connected' }
          if (!transport) {
            const n = (a.serverAttempts ?? 0) + 1
            next.serverAttempts = n
            next.nextAttemptAt = now + backoffMs(n)
            if (n >= MAX_SERVER_ATTEMPTS) {
              next.failed = true
              failedNow = next
            }
          }
          return next
        })
        saveQueue(q, storage)
        if (failedNow) result.failed.push(failedNow)
        // No connection: everything after this would fail the same way.
        if (transport) break
        blocked.add(res)
        continue
      }
      removeAction(action.id, storage)
      result.refused.push({ action, message: message ?? 'Refused' })
    }
  }
  result.stillQueued = loadQueue(storage).length
  return result
}

/** "Try again": a failed action goes back to waiting, its count reset. */
export function retryAction(id: string, storage: KVStorage | null = defaultStorage()): QueuedAction[] {
  const q = loadQueue(storage).map((a) =>
    a.id === id ? { ...a, failed: false, serverAttempts: 0, nextAttemptAt: undefined } : a,
  )
  saveQueue(q, storage)
  return q
}

/** The action waiting on this tablet for this route, if any (e.g. one requisition's receive). */
export function queuedFor(queue: QueuedAction[], method: QueuedMethod, path: string): QueuedAction | undefined {
  return queue.find((a) => a.method === method && a.path === path)
}

export interface RunOptions {
  /** The caller's intent key; reused on every retry of the same intent. */
  key?: string
  /**
   * At most one waiting action for this route: a re-tap while one is queued
   * is not sent again (a second receive would move stock twice on sync).
   */
  exclusive?: boolean
}

export type RunPlan = { kind: 'send'; key: string } | { kind: 'already-queued'; action: QueuedAction }

/**
 * Decides what a kitchen action does before it is sent. A route that already
 * has a waiting action is skipped when exclusive; the same request already
 * waiting (same route and body) reuses that action's key so a send now and
 * the queued retry are one action to the server.
 */
export function planRun(
  queue: QueuedAction[],
  method: QueuedMethod,
  path: string,
  body: unknown,
  opts: RunOptions = {},
  mint: () => string = newIdempotencyKey,
): RunPlan {
  const waiting = queuedFor(queue, method, path)
  if (waiting && opts.exclusive) return { kind: 'already-queued', action: waiting }
  const same = queue.find((a) => a.method === method && a.path === path && JSON.stringify(a.body ?? null) === JSON.stringify(body ?? null))
  if (same) return { kind: 'send', key: same.id }
  return { kind: 'send', key: opts.key ?? mint() }
}
